import React, { useState, useCallback } from 'react';
import { IStudentStats } from 'models/IStats';
import './StudentPerformanceChart.css';

interface StudentPerformanceChartProps {
    students: IStudentStats[];
}

interface TooltipData {
    name: string;
    accuracy: number;
    thinkRank: number;
    solved: number;
    x: number;
    y: number;
}

const StudentPerformanceChart: React.FC<StudentPerformanceChartProps> = ({ students }) => {
    const [tooltip, setTooltip] = useState<TooltipData | null>(null);

    // Calculate bounds
    let minThinkRank, maxThinkRank, minAccuracy, maxAccuracy;
    
    if (students.length === 0) {
        // No students case - use center values with padding
        minThinkRank = 45;
        maxThinkRank = 55;
        minAccuracy = 45;
        maxAccuracy = 55;
    } else if (students.length === 1) {
        // Single student case - use center values ±0.05 for ThinkRank, ±5 for Accuracy
        const student = students[0];
        minThinkRank = Math.max(0, student.think_rank - 5);
        maxThinkRank = Math.min(100, student.think_rank + 5);
        minAccuracy = Math.max(0, student.accuracy_rate - 5);
        maxAccuracy = Math.min(100, student.accuracy_rate + 5);
    } else {
        // Multiple students case - calculate actual bounds
        minThinkRank = Math.min(...students.map(s => s.think_rank));
        maxThinkRank = Math.max(...students.map(s => s.think_rank));
        minAccuracy = Math.min(...students.map(s => s.accuracy_rate));
        maxAccuracy = Math.max(...students.map(s => s.accuracy_rate));

        // Add padding to bounds
        const thinkRankPadding = (maxThinkRank - minThinkRank) * 0.05;
        const accuracyPadding = (maxAccuracy - minAccuracy) * 0.05;
        minThinkRank = Math.max(0, minThinkRank - thinkRankPadding);
        maxThinkRank = Math.min(100, maxThinkRank + thinkRankPadding);
        minAccuracy = Math.max(0, minAccuracy - accuracyPadding);
        maxAccuracy = Math.min(100, maxAccuracy + accuracyPadding);
    }

    // Final bounds
    const thinkRankMin = minThinkRank;
    const thinkRankMax = maxThinkRank;
    const accuracyMin = minAccuracy;
    const accuracyMax = maxAccuracy;

    // SVG dimensions - match the HTML template
    const chartWidth = 675;
    const chartHeight = 400;
    const margin = { top: 50, right: 26, bottom: 50, left: 50 };
    const plotWidth = 600; // Fixed width as in template
    const plotHeight = 300; // Fixed height as in template

    // Scale functions
    const xScale = useCallback((thinkRank: number) => 
        margin.left + ((thinkRank - thinkRankMin) / (thinkRankMax - thinkRankMin)) * plotWidth,
        [margin.left, thinkRankMin, thinkRankMax, plotWidth]);
    
    const yScale = useCallback((accuracy: number) => 
        margin.top + ((accuracyMax - accuracy) / (accuracyMax - accuracyMin)) * plotHeight,
        [margin.top, accuracyMax, accuracyMin, plotHeight]);

    // Calculate 75th percentile for circle size formula
    const solvedValues = students.map(s => s.solved).sort((a, b) => a - b);
    const percentile75Index = Math.floor(0.75 * solvedValues.length);
    const M = solvedValues[percentile75Index] || 1; // M is the 75th percentile, default to 1 to avoid division by zero

    // Calculate circle radius based on new formula: f(x) = 30 * arctan(x/M)
    const getRadius = (solved: number) => {
        return 20 * Math.atan(solved / M);
    };

    // Grid lines - dense grid as in template
    const xTicks = 15; // More ticks for dense grid
    const yTicks = 9;  // More ticks for dense grid
    const xTickValues = Array.from({ length: xTicks }, (_, i) => 
        thinkRankMin + (thinkRankMax - thinkRankMin) * i / (xTicks - 1)
    );
    const yTickValues = Array.from({ length: yTicks }, (_, i) => 
        accuracyMin + (accuracyMax - accuracyMin) * i / (yTicks - 1)
    );
    
    // Labels only for key values
    const xLabelTicks = 8;
    const yLabelTicks = 6;
    const xLabelValues = Array.from({ length: xLabelTicks }, (_, i) => 
        thinkRankMin + (thinkRankMax - thinkRankMin) * i / (xLabelTicks - 1)
    );
    const yLabelValues = Array.from({ length: yLabelTicks }, (_, i) => 
        accuracyMin + (accuracyMax - accuracyMin) * i / (yLabelTicks - 1)
    );

    const handleMouseMove = useCallback((event: React.MouseEvent<SVGCircleElement>, student: IStudentStats) => {
        const circleElement = event.currentTarget;
        const containerRect = circleElement.closest('.chart-container-inner')?.getBoundingClientRect();
        
        if (!containerRect) return;

        // Получаем реальные координаты circle элемента на экране (это работало лучше)
        const circleRect = circleElement.getBoundingClientRect();
        
        // Получаем радиус из SVG атрибута (это точнее чем из getBoundingClientRect)
        const r = parseFloat(circleElement.getAttribute('r') || '0');
        
        // Вычисляем центр кружочка относительно контейнера
        const centerX = circleRect.left - containerRect.left + circleRect.width / 2;
        const centerY = circleRect.top - containerRect.top + circleRect.height / 2;
        
        // Получаем SVG для определения масштаба радиуса
        const svgElement = circleElement.closest('svg');
        const svgRect = svgElement?.getBoundingClientRect();
        
        // Конвертируем SVG радиус в экранные пиксели
        const scaleY = svgRect ? svgRect.height / chartHeight : 1;
        const screenRadius = r * scaleY;
        
        // Позиционируем попап точно над кружочком
        const tooltipX = centerX;
        const tooltipY = centerY - screenRadius  - 37; // поднимаем попап вверх на 50%
        
        setTooltip({
            name: student.name,
            accuracy: student.accuracy_rate,
            thinkRank: student.think_rank,
            solved: student.solved,
            x: tooltipX,
            y: tooltipY,
        });
    }, [chartHeight]);

    const handleMouseLeave = useCallback(() => {
        setTooltip(null);
    }, []);

    return (
        <div className="chart-container-inner">
            <svg
                className="chart-svg"
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            >
                {/* Dense grid as in template */}
                <defs>
                    <clipPath id="gridClip">
                        <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} />
                    </clipPath>
                </defs>
                
                {/* Main chart area */}
                <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} fill="none" stroke="#d1d1d1" strokeWidth={1}/>
                
                {/* Dense grid lines */}
                <g clipPath="url(#gridClip)" className="chart-grid">
                    {/* Vertical grid lines */}
                    {xTickValues.map((value, i) => (
                        <line
                            key={`vgrid-${i}`}
                            className="grid-line vertical-grid"
                            x1={xScale(value)}
                            y1={margin.top}
                            x2={xScale(value)}
                            y2={margin.top + plotHeight}
                            stroke="#e5e7eb"
                            strokeWidth={1}
                        />
                    ))}
                    
                    {/* Horizontal grid lines */}
                    {yTickValues.map((value, i) => (
                        <line
                            key={`hgrid-${i}`}
                            className="grid-line horizontal-grid"
                            x1={margin.left}
                            y1={yScale(value)}
                            x2={margin.left + plotWidth}
                            y2={yScale(value)}
                            stroke="#e5e7eb"
                            strokeWidth={1}
                        />
                    ))}
                </g>


                {/* X-axis labels - only for key values */}
                {xLabelValues.map((value, i) => (
                    <text
                        key={`xlabel-${i}`}
                        x={xScale(value)}
                        y={margin.top + plotHeight + 17}
                        textAnchor="middle"
                        className="axis-label"
                    >
                        {value.toFixed(0)}
                    </text>
                ))}

                {/* Y-axis labels - only for key values */}
                {yLabelValues.map((value, i) => (
                    <text
                        key={`ylabel-${i}`}
                        x={margin.left - 6}
                        y={yScale(value) + 4}
                        textAnchor="end"
                        className="axis-label"
                    >
                        {value.toFixed(0)}
                    </text>
                ))}

                {/* Axis titles */}
                <text
                    x={margin.left + plotWidth / 2}
                    y={chartHeight - 12}
                    textAnchor="middle"
                    className="axis-title"
                >
                    ThinkRank
                </text>
                <text
                    x={16}
                    y={margin.top + plotHeight / 2}
                    textAnchor="middle"
                    className="axis-title"
                    transform={`rotate(-90 16 ${margin.top + plotHeight / 2})`}
                >
                    Accuracy Rate
                </text>

                {/* Shadow circles (semi-transparent) */}
                <g opacity={0.3}>
                    {students.map((student, i) => (
                        <circle
                            key={`shadow-${i}`}
                            cx={xScale(student.think_rank)}
                            cy={yScale(student.accuracy_rate)}
                            r={getRadius(student.solved)}
                            fill="#6366f1"
                            fillOpacity={0.5}
                        />
                    ))}
                </g>

                {/* Main data points */}
                <g>
                    {students.map((student, i) => (
                        <g key={`student-${i}`}>
                            <circle
                                className="student-point"
                                cx={xScale(student.think_rank)}
                                cy={yScale(student.accuracy_rate)}
                                r={getRadius(student.solved)}
                                data-name={student.name}
                                data-accuracy={`${student.accuracy_rate.toFixed(1)}%`}
                                data-rank={student.think_rank.toFixed(0)}
                                data-solved={student.solved}
                                onMouseMove={(e) => handleMouseMove(e, student)}
                                onMouseLeave={handleMouseLeave}
                                style={{
                                    filter: tooltip?.name === student.name ? 'brightness(1.2)' : 'none'
                                }}
                            />
                            <text
                                x={xScale(student.think_rank)}
                                y={yScale(student.accuracy_rate) - getRadius(student.solved) - 5}
                                textAnchor="middle"
                                className="student-label"
                            >
                                {student.name.length > 15 ? student.name.substring(0, 15) + '...' : student.name}
                            </text>
                        </g>
                    ))}
                </g>
            </svg>

            {/* Tooltip */}
            {tooltip && (
                <div 
                    className="chart-tooltip show"
                    style={{
                        left: `${tooltip.x}px`,
                        top: `${tooltip.y - 60}px`, // Стрелочка касается верхней части круга
                        transform: 'translateX(-50%)', // Центрируем попап по горизонтали
                        position: 'absolute'
                    }}
                >
                    <div><strong>{tooltip.name}</strong></div>
                    <div>Accuracy: {tooltip.accuracy.toFixed(1)}%</div>
                    <div>Think Rank: {tooltip.thinkRank.toFixed(0)}</div>
                    <div>Tasks Solved: {tooltip.solved}</div>
                </div>
            )}
        </div>
    );
};

export default StudentPerformanceChart;